import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from './Button.tsx'
import { Card } from './Card.tsx'
import { Input } from './Input.tsx'
import { cn } from '@/lib/utils.ts'

export interface LoginCardProps {
  title: string
  subtitle?: string
  iconClassName?: string
  iconLabel?: string
  m365LoginUrl: string
  errorMessages?: Record<string, string>
  onPasswordLogin: (email: string, password: string) => Promise<void>
  footerHint?: ReactNode
  /**
   * Tailwind-Klassen fuer den aeusseren Wrapper. Default zentriert die Card
   * vertikal auf Viewport-Hoehe (`min-h-screen`). Uebersteuerbar z.B. fuer
   * Previews im UI-Kit (`className="min-h-[480px]"`).
   */
  wrapperClassName?: string
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

export function LoginCard({
  title,
  subtitle,
  iconClassName,
  iconLabel = 'i',
  m365LoginUrl,
  errorMessages,
  onPasswordLogin,
  footerHint,
  wrapperClassName,
}: LoginCardProps) {
  const [params] = useSearchParams()
  const urlErrorCode = params.get('error')
  const urlErrorEmail = params.get('email')
  const urlErrorMessage = urlErrorCode
    ? errorMessages?.[urlErrorCode] ?? `Fehler: ${urlErrorCode}`
    : null

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    setSubmitting(true)
    try {
      await onPasswordLogin(email.trim(), password)
    } catch (err) {
      const ax = err as { response?: { data?: { error?: string; message?: string } }; message?: string }
      setSubmitError(
        ax.response?.data?.error ?? ax.response?.data?.message ?? ax.message ?? 'Anmeldung fehlgeschlagen',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={cn('min-h-screen flex items-center justify-center bg-gray-50 px-4', wrapperClassName)}>
      <Card className="w-full max-w-md">
        <Card.Header>
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded flex items-center justify-center flex-shrink-0',
                iconClassName ?? 'bg-brand-700',
              )}
            >
              <span className="text-white font-bold text-lg leading-none">{iconLabel}</span>
            </div>
            <div>
              <h1 className="text-h3 text-brand-700">{title}</h1>
              {subtitle ? <p className="text-small text-gray-500">{subtitle}</p> : null}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {urlErrorMessage ? (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-small text-red-800">
              <div className="font-medium">Anmeldung fehlgeschlagen</div>
              <div>{urlErrorMessage}</div>
              {urlErrorEmail ? <div className="text-caption mt-1 text-red-700">({urlErrorEmail})</div> : null}
            </div>
          ) : null}

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            leadingIcon={<MicrosoftLogo />}
            onClick={() => {
              window.location.href = m365LoginUrl
            }}
          >
            Mit Microsoft 365 anmelden
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-caption">
              <span className="bg-white px-2 text-gray-400">oder</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {submitError ? (
              <div className="p-2 rounded bg-red-50 border border-red-200 text-small text-red-800">{submitError}</div>
            ) : null}
            <Input
              label="E-Mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
            <Input
              label="Passwort"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" loading={submitting}>
              {submitting ? 'Wird angemeldet …' : 'Anmelden'}
            </Button>
          </form>

          {footerHint ? <p className="text-small text-gray-500 mt-4">{footerHint}</p> : null}
        </Card.Body>
      </Card>
    </div>
  )
}
